import React from 'react';
import type { ProjectListItem } from '../types';

/** ISO string → 'dd/mm' (pt-BR) para os rótulos das pontas; '' se ausente/inválida. */
function fmtDay(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** ISO string → epoch ms (ou `null` se ausente/inválida). */
function toMs(iso?: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

export interface MiniGanttProps {
  /** Item de listagem (traz período agregado + progresso + próximo marco). */
  project: ProjectListItem;
}

/**
 * Timeline em miniatura para o card de listagem de projetos.
 *
 * Diferente do Gantt do workspace, o `ProjectListItem` NÃO traz as tarefas por
 * fase — só o período total (`startDate`/`endDate`), o progresso agregado
 * (`progress.pct`) e o próximo marco (`nextMilestone`). A mini-timeline é
 * honesta com esses dados:
 *  - um trilho fino (8px, pílula) do período total preenchido na proporção de
 *    `progress.pct` na cor primária;
 *  - um losango vazado (borda de alerta) na data do próximo marco;
 *  - uma linha tracejada "hoje", se a data atual estiver dentro do período;
 *  - as datas de início/fim nas pontas do trilho.
 *
 * Quando o projeto não tem período (sem tarefas datadas), exibe um placeholder
 * "Sem cronograma".
 */
export const MiniGantt: React.FC<MiniGanttProps> = ({ project }) => {
  const start = toMs(project.startDate);
  const end = toMs(project.endDate);
  const hasRange = start !== null && end !== null && end > start;
  const pct = Math.max(0, Math.min(100, project.progress?.pct ?? 0));

  const posOf = (ms: number): number =>
    hasRange ? Math.max(0, Math.min(100, ((ms - start) / (end - start)) * 100)) : 0;

  if (!hasRange) {
    return (
      <div
        className="flex items-center justify-center rounded-md"
        style={{
          height: 30,
          fontSize: 11,
          backgroundColor: 'var(--theme-surface-sunken)',
          color: 'var(--theme-muted-text)',
        }}
      >
        Sem cronograma
      </div>
    );
  }

  const nowMs = Date.now();
  const todayPos = nowMs >= start && nowMs <= end ? posOf(nowMs) : null;

  const milestoneMs = toMs(project.nextMilestone?.date ?? null);
  const milestonePos = milestoneMs !== null ? posOf(milestoneMs) : null;

  return (
    <div style={{ margin: '2px 0' }}>
      {/* Trilho fino do período + preenchimento (concluído) */}
      <div
        className="relative rounded-full"
        style={{ height: 8, backgroundColor: 'var(--theme-surface-sunken)' }}
      >
        <div
          className="absolute left-0 top-0 h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: 'var(--theme-primary)' }}
        />

        {/* Linha "hoje" */}
        {todayPos !== null && (
          <div
            className="absolute"
            style={{
              left: `${todayPos}%`,
              top: -4,
              bottom: -4,
              width: 0,
              borderLeft: '1.5px dashed var(--theme-muted-text)',
              zIndex: 2,
            }}
          />
        )}

        {/* Losango vazado do próximo marco */}
        {milestonePos !== null && (
          <div
            className="absolute"
            title={project.nextMilestone ? `◆ ${project.nextMilestone.title}` : undefined}
            style={{
              left: `${milestonePos}%`,
              top: -1,
              width: 10,
              height: 10,
              transform: 'translateX(-50%) rotate(45deg)',
              borderRadius: 2,
              backgroundColor: 'var(--theme-card-background)',
              border: '1.6px solid var(--theme-warning-color)',
              zIndex: 3,
            }}
          />
        )}
      </div>

      {/* Datas nas pontas */}
      <div
        className="flex justify-between"
        style={{ marginTop: 8, fontSize: 11, color: 'var(--theme-muted-text)' }}
      >
        <span>{fmtDay(project.startDate)}</span>
        <span>{fmtDay(project.endDate)}</span>
      </div>
    </div>
  );
};

MiniGantt.displayName = 'MiniGantt';
